/** @format */

import { createSelector } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "src/redux";
import { setReduxLoading } from "src/redux/actions/loadingReduxActions";
import { handleError, logDev } from "src/utils";
import { useCancellable } from "../useCancellable";

const loadingReduxState = (state: IAppState) => state?.loadingRedux;

export const isLoadingSelector = createSelector(
    [loadingReduxState],
    (loadingRedux: { isLoading: boolean }) => {
        return loadingRedux.isLoading;
    },
);

export const useLoadingRedux = (
    changeConditions: any[] = [],
): [
    boolean,
    (load: boolean) => void,
    () => void,
    (error: Error, errorHandler?: (e: Error) => void) => void,
] => {
    const changes = useMemo(() => changeConditions, [changeConditions]);
    const dispatch = useDispatch();
    const makeCancellable = useCancellable();
    const isLoading = useSelector(isLoadingSelector);

    const setLoading = useCallback(
        (loading: boolean) => {
            dispatch(setReduxLoading(loading));
        },
        [dispatch],
    );

    const stopLoading = useCallback(() => setLoading(false), [setLoading]);

    const stopLoadingOnError = useCallback(
        () => (error: Error, errorHandler?: (e: Error) => void) => {
            stopLoading();
            errorHandler ? errorHandler(error) : handleError(error);
        },
        [stopLoading],
    );

    useEffect(() => {
        const promise = Promise.resolve(() => {
            if (isLoading) {
                setLoading(false);
            }
        }).catch(stopLoadingOnError) as Promise<() => void>;

        const timeout = setTimeout(
            () =>
                Promise.resolve(
                    makeCancellable(promise, () =>
                        logDev("useLoading - Canceled loading indicator timeout"),
                    )
                        .then((f: () => void) => f())
                        .catch(stopLoadingOnError),
                ),
            2000,
        );
        return () => {
            clearTimeout(timeout);
        };

        // eslint-disable-next-line
    }, [changes, isLoading, makeCancellable, setLoading, stopLoading, stopLoadingOnError]);

    return [isLoading, setLoading, stopLoading, stopLoadingOnError];
};
